// External Dependencies
import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

// Icons
import { IoArrowBackOutline } from 'react-icons/io5'

// Config and Constants
import { Permission } from '../../config/Permission'

// Hooks and Utils
import useGoBack from '../../hooks/useGoBack'
import useUserStore from '../../utils/state/useUserStore'
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'

// Components
import CustomToast from '../../components/Custom/CustomToast'
import PageHeader from '../../components/Custom/PageHeader/PageHeader'

const RoleUpdate = () => {
  // Hooks
  const navigate = useNavigate()
  const goBack = useGoBack()
  const { id } = useParams()
  const userPermissions = useUserStore((state) => state.user) || []

  // State Management
  const [isLoading, setLoading] = useState(true)
  const [formData, setFormData] = useState({
    roleName: '',
    permissions: [],
  })
  const [isUpdating, setUpdating] = useState(false)
  const [error, setError] = useState(null)

  // Fetch role data on component mount
  useEffect(() => {
    const fetchRoleData = async () => {
      try {
        const response = await axiosInterceptorInstance.get(`/roles/role/${id}`)
        const roleData = response.data.role

        setFormData({
          roleName: roleData.name || '',
          permissions: roleData.permissions || [],
        })
      } catch (error) {
        CustomToast({
          message: 'Failed to fetch role details',
          type: 'error',
        })
        navigate('/role')
      } finally {
        setLoading(false)
      }
    }

    fetchRoleData()
  }, [id, navigate])

  // Handle permission toggle
  const togglePermission = (value) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(value)
        ? prev.permissions.filter((p) => p !== value)
        : [...prev.permissions, value],
    }))
    setError(null)
  }

  // Form validation
  const validateForm = () => {
    if (!formData.roleName.trim()) {
      setError('Role name is required')
      return false
    }
    if (formData.permissions.length === 0) {
      setError('At least one permission is required')
      return false
    }
    return true
  }

  // Handle form submission
  const handleSubmit = async () => {
    if (!userPermissions.includes('role:update')) {
      CustomToast({
        message: 'You do not have permission to update roles',
        type: 'error',
      })
      return
    }

    if (!validateForm()) return

    setError(null)
    setUpdating(true)

    try {
      await axiosInterceptorInstance.put(`/roles/role/${id}`, {
        roleName: formData.roleName,
        permissions: formData.permissions,
      })

      CustomToast({
        message: 'Role updated successfully',
        type: 'success',
      })
      navigate('/role')
    } catch (error) {
      const errorMessage =
        error.response?.status === 422
          ? 'Role name already exists'
          : 'Failed to update role. Please try again'

      setError(errorMessage)
      CustomToast({
        message: errorMessage,
        type: 'error',
      })
    } finally {
      setUpdating(false)
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <p className='text-lg text-gray-600'>Loading role details...</p>
      </div>
    )
  }

  return (
    <div>
      <PageHeader>
        <div className='flex gap-2 items-center justify-between w-full'>
          <div className='flex gap-2 items-center font-medium'>
            <button
              onClick={goBack}
              className='h-9 w-9 flex justify-center items-center rounded-full text-xl cursor-pointer transition-all duration-300 hover:text-white hover:bg-prime'
              aria-label='Go back'
            >
              <IoArrowBackOutline />
            </button>
            <h1 className='text-2xl'>Update Role</h1>
          </div>
        </div>
      </PageHeader>

      <div className='w-full sm:w-[500px] mx-auto'>
        <label className='block text-sm font-medium mb-2' htmlFor='roleName'>
          Role Name:
        </label>
        <input
          id='roleName'
          type='text'
          placeholder='Enter Role Name'
          className='w-full border-[2px] border-light-gray focus:border-prime rounded-lg p-3 focus:outline-none focus:border-orange invalid:border-red-700'
          value={formData.roleName}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, roleName: e.target.value }))
            setError(null)
          }}
        />

        {/* Permission Categories */}
        {Object.entries(Permission).map(([category, perms]) => (
          <div key={category} className='my-4 p-2'>
            <h3 className='font-medium mb-2'>{category}</h3>
            <div className='flex flex-wrap gap-4'>
              {Object.entries(perms).map(([action, value]) => (
                <label
                  key={value}
                  className='space-x-2 flex items-center cursor-pointer'
                >
                  <input
                    type='checkbox'
                    className='w-5 h-5 accent-prime cursor-pointer'
                    checked={formData.permissions.includes(value)}
                    onChange={() => togglePermission(value)}
                  />
                  <span>{action}</span>
                </label>
              ))}
            </div>
          </div>
        ))}

        {/* Error Message */}
        {error && (
          <div className='text-red-700 text-sm my-4 font-normal' role='alert'>
            {error}
          </div>
        )}

        {/* Submit Button */}
        <button
          disabled={isUpdating}
          onClick={handleSubmit}
          className='w-full py-3 rounded-md sm:w-[500px] text-prime bg-white border border-prime hover:bg-prime hover:text-white transition-all duration-300 disabled:bg-prime disabled:pointer-events-none disabled:cursor-auto disabled:bg-opacity-80 disabled:text-white'
        >
          {isUpdating ? 'Updating...' : 'Update Role'}
        </button>
      </div>
    </div>
  )
}

export default RoleUpdate
